import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ApplicationHostTypeService,
  HistoryService,
  ReferenceDataCategoriesConstants,
  ReferenceDataConstants,
  ReferenceDataLabels,
  ReferenceDataOptionLists,
  ReferenceDataService,
  RomeNotificationService,
  UserSessionService
} from 'rome-library';
import { UserCredentialStore } from '../../../interfaceTypes/sharedFeatures/registration/services/user-credential.store';
import { States } from '../../../shared';
import { TaggingService } from '../../../tag-manager';
import { AuthorizationFlowConstants } from '../../constants/authorization-flow-constants';
import { RegistrationConfirmationStatusViewModel } from '../../models/registration-confirmation-status-viewmodel.model';
import { CookieService } from '../../services/cookie.service';
import { LoginService } from '../../services/login.service';
import { NotificationLoginService } from '../../services/notification-login.service';
import { StateService } from '../../services/state.service';
import { SystemAvailabilityService } from '../../services/system-availability.service';
import { TokenStore } from '../../services/token.store';
import { UserService } from '../../services/user.service';

@UntilDestroy()
@Component({
  selector: 'boi-login',
  templateUrl: './login.page.html'
})
export class LoginPage implements OnInit {
  public readonly referenceDataLabels = ReferenceDataLabels;
  public readonly referenceDataOptionLists = ReferenceDataOptionLists;
  public readonly referenceDataConstants = ReferenceDataConstants;
  public readonly referenceDataCategoriesConstants = ReferenceDataCategoriesConstants;
  public username: string;
  public password: string;
  public notificationType: string;
  public errorMessage: string;
  public readonly registrationConfirmationStatus = RegistrationConfirmationStatusViewModel;
  public isValidationOtpForm: boolean;
  public otpCode: string = '';
  public hasExceededMaximumOtpSendLimit: boolean;
  public hasAnOtpCodeActive: boolean;
  public hasUserExceededMaximumOtpValidation: boolean;
  public isOtpCodeInvalid: boolean;
  public maskedPhoneNumber: string;

  constructor(
    private readonly userSessionService: UserSessionService,
    private readonly loginService: LoginService,
    private readonly tokenStore: TokenStore,
    private readonly router: Router,
    private readonly historyService: HistoryService,
    private readonly userService: UserService,
    private readonly applicationHostTypeService: ApplicationHostTypeService,
    private readonly notificationLoginService: NotificationLoginService,
    private readonly referenceDataService: ReferenceDataService,
    private readonly notificationService: RomeNotificationService,
    private readonly stateService: StateService,
    private readonly userCredentialStore: UserCredentialStore,
    private readonly taggingService: TaggingService,
    private readonly cookieService: CookieService,
    private readonly systemAvailabilityService: SystemAvailabilityService
  ) {}

  ngOnInit(): void {
    this.getApplicationSystemAvailability();

    this.taggingService.resetDataLayer();
    this.taggingService.execute();

    this.notificationType = this.notificationLoginService.getNotificationType();

    this.username = this.userCredentialStore.getEmail();
    if (this.userCredentialStore.isExpiredPasswordSet()) {
      this.password = this.userCredentialStore.getExpiredPassword();
    }

    if (this.tokenStore.getSessionToken()) {
      this.loginService.logout(States.Login).subscribe();
    }

    this.userService.setUser(undefined);
  }

  private getApplicationSystemAvailability(): void {
    this.systemAvailabilityService.getApplicationSystemAvailability().pipe(untilDestroyed(this)).subscribe();
  }

  login(loginForm?: NgForm): void {
    if (loginForm?.invalid) {
      return;
    }

    this.onLogin();
  }

  public loginWithOtp(): void {
    if (this.otpCode) {
      this.onLogin();
    }
  }

  private onLogin(): void {
    const applicationVersion = this.referenceDataService.getApplicationCurrentVersion();

    this.loginService
      .login(this.username, this.password, this.otpCode)
      .pipe(untilDestroyed(this))
      .subscribe(
        ([_, hasAcceptedCookies, currentApplicationConfiguration]) => {
          if (currentApplicationConfiguration === applicationVersion) {
            this.cookieService.hasAcceptedCookies = hasAcceptedCookies;

            const previousUrl = this.historyService.previousUrl;
            const userContext = this.tokenStore.getUserContext();

            const user = this.userService.getUser();
            if (!user.ProfileComplete) {
              void this.router.navigate([
                this.applicationHostTypeService.getCode(),
                this.stateService.getUserProfileState(),
                user.UserId
              ]);
            } else if (userContext.IsFirmStartingInTheFuture) {
              void this.router.navigate([
                this.applicationHostTypeService.getCode(),
                States.WritingBusiness,
                user.UserId
              ]);
            } else if (this.isUrlValid(previousUrl)) {
              void this.router.navigateByUrl(previousUrl);
            } else {
              void this.router.navigate([
                this.applicationHostTypeService.getCode(),
                this.stateService.getAfterLoginState()
              ]);
            }

            this.userCredentialStore.setExpiredPassword(undefined);
            this.userCredentialStore.setEmail(undefined);
            this.historyService.initialRedirectUrl = undefined;
            this.userSessionService.restartSession();
          } else {
            this.notificationService.showError(
              this.referenceDataService.getLocalizeLabel(ReferenceDataLabels.NewApplicationVersionAvailableMessage)
            );
            window.location.replace(window.location.pathname);
          }
        },
        (errorResponse) => {
          if (AuthorizationFlowConstants.ExpiredPasswordStatuses.includes(errorResponse.error.error_description)) {
            this.userCredentialStore.setExpiredPassword(this.password);
            this.userCredentialStore.setEmail(this.username);

            this.goToExpiredPassword();
          } else if (AuthorizationFlowConstants.ValidationOtpRequired === errorResponse.error.error) {
            this.isValidationOtpForm = true;
            this.hasExceededMaximumOtpSendLimit = errorResponse.error?.has_exceeded_maximum_otp_send_limit;
            this.hasAnOtpCodeActive = errorResponse.error?.has_an_otp_code_active;
            this.hasUserExceededMaximumOtpValidation = errorResponse.error?.has_user_exceeded_maximum_otp_validation;
            this.maskedPhoneNumber = errorResponse.error?.masked_phone_number;
          } else if (AuthorizationFlowConstants.ValidationOtpInvalid === errorResponse.error.error) {
            this.isOtpCodeInvalid = true;
            this.hasAnOtpCodeActive = errorResponse.error?.has_an_otp_code_active;
            this.hasUserExceededMaximumOtpValidation = errorResponse.error?.has_user_exceeded_maximum_otp_validation;
          } else {
            this.setErrorMessageByDescription(errorResponse.error.error_description);
          }
        }
      );
  }

  private isUrlValid(url: string): boolean {
    return (
      url &&
      !url.includes(States.ForgottenPassword) &&
      !url.includes(States.ResetForgottenPassword) &&
      !url.includes(States.ExpiredPassword) &&
      url !== `/${this.applicationHostTypeService.getCode()}/${States.Login}` &&
      url !== '/' &&
      url.indexOf(`/${States.Agent}/`) < 0 &&
      url.indexOf(`/${States.DirectActivation}/`) < 0 &&
      url.indexOf(`/${States.OnlineOffer}/`) < 0 &&
      url.indexOf(`/${States.Switch}/`) < 0
    );
  }

  public goToForgotPassword(): void {
    if (this.userCredentialStore.isEmailValid(this.username)) {
      this.userCredentialStore.setEmail(this.username);
    }

    void this.router.navigate([this.applicationHostTypeService.getCode(), States.ForgottenPassword]);
  }

  public goToExpiredPassword(): void {
    void this.router.navigate([this.applicationHostTypeService.getCode(), States.ExpiredPassword]);
  }

  public setErrorMessageByDescription(errorDescription: string): void {
    const errorValue = this.referenceDataService.getConfigurationValueByCode(
      this.referenceDataCategoriesConstants.ERROR_MESSAGE,
      this.referenceDataConstants.ERROR_MESSAGE_USER_ACCOUNT,
      errorDescription
    );

    if (errorValue) {
      this.errorMessage = errorValue;
    }
  }

  public sendCode(): void {
    this.loginService
      .login(this.username, this.password)
      .pipe(untilDestroyed(this))
      .subscribe(
        () => {},
        (errorResponse) => {
          if (AuthorizationFlowConstants.ValidationOtpRequired === errorResponse.error.error) {
            this.hasAnOtpCodeActive = errorResponse.error?.has_an_otp_code_active;
            this.hasExceededMaximumOtpSendLimit = errorResponse.error?.has_exceeded_maximum_otp_send_limit;
            this.hasUserExceededMaximumOtpValidation = errorResponse.error?.has_user_exceeded_maximum_otp_validation;
            this.isOtpCodeInvalid = false;
          }
        }
      );
  }

  public onBackToLogin(): void {
    this.isValidationOtpForm = false;
    this.otpCode = null;
    this.isOtpCodeInvalid = null;
  }
}
